declare const Worker: any;
declare const window: any;
// Note: elkjs needs to be installed: bun add elkjs
import ELKApi from 'elkjs/lib/elk-api.js';
import { Node, Edge } from '../../../core/models/scene';
import { getShapeManifest } from '../../../lib/diagram/registries';

export interface LayoutOptions {
  strategy: 'left-to-right' | 'top-to-bottom' | 'hierarchical' | 'compact';
  nodeSpacing?: number;      // Horizontal gap between nodes (default: 80)
  rankSpacing?: number;      // Vertical gap between ranks/levels (default: 100)
  respectLanes?: boolean;    // Keep nodes within their assigned lanes (future)
  animate?: boolean;         // Animate transition to new positions (future)
}

export type ElkPoint = { x: number; y: number };
export interface ElkEdgeEndpoints {
  start: ElkPoint; // Source attachment point (in ELK graph coordinates)
  end: ElkPoint;   // Target attachment point (in ELK graph coordinates)
}

export interface LayoutResult {
  nodePositions: Map<string, { x: number; y: number }>;
  edgeEndpoints: Map<string, ElkEdgeEndpoints>;
}

/**
 * Creates an ELK layout engine across every runtime we deploy to.
 *
 * - Browser (Vite): blob URL → Web Worker via elk-api.
 * - Bun/Node: file path → native Worker via elk-api.
 * - Cloudflare Workers (and other ESM-only no-Worker runtimes): dynamic
 *   ESM import of `elk.bundled.js` to run layout inline. CF Workers
 *   don't have `require` or `Worker`, so the previous code returned a
 *   no-op layout — which is why diagrams materialized server-side
 *   landed with the hydrator's dumb grid (broken swimlanes, mangled
 *   connector routes). The bundled variant is ~1MB but only loads on
 *   first use.
 *
 * Returns a promise so the no-Worker branch can `await import` lazily.
 */
async function createElk(): Promise<{ layout: (graph: any) => Promise<any> }> {
  const isBrowser = typeof window !== 'undefined';
  const hasRequire = typeof require === 'function';
  if (isBrowser) {
    // In browser: dynamic import of ?url gets the Vite-transformed blob URL.
    // Use elk-api + workerUrl for threaded layout.
    // Fall through to workerFactory approach if ?url doesn't resolve.
    try {
      if (hasRequire) {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const elkWorkerUrl = require('elkjs/lib/elk-worker.min.js?url');
        const url = typeof elkWorkerUrl === 'string' ? elkWorkerUrl : elkWorkerUrl?.default;
        if (url && typeof url === 'string') {
          return new (ELKApi as any)({ workerUrl: url });
        }
      }
    } catch { /* fall through */ }
    // Fallback: try bundled (works if elk-worker.min.js Worker export is defined)
    try {
      if (hasRequire) {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const ELKBundled = require('elkjs/lib/elk.bundled.js');
        return new ELKBundled();
      }
    } catch { /* fall through */ }
    // Last-ditch: same dynamic-import path the CF Worker uses. ESM
    // browsers without ?url support still get layout this way.
    try {
      const ELKBundled = await loadBundledElk();
      if (ELKBundled) return new ELKBundled();
    } catch { /* fall through */ }
    console.warn(
      '[ProcessLayoutService] elkjs worker URL did not resolve via Vite ?url or the bundled fallback — ' +
      'falling back to the deterministic layered layout.',
    );
    return DETERMINISTIC_FALLBACK_ELK;
  }

  // Server-side. We try the worker-thread path first only if it's
  // genuinely available, because Cloudflare Workers ship a `__require`
  // polyfill that satisfies `typeof require === 'function'` but throws
  // on `require.resolve` — which would knock out every layout. The
  // bundled (in-thread) path works in CF Workers, Bun, and Node, so
  // it's the safer default; we fall back to the worker-thread path
  // only when both `require.resolve` and a real `Worker` exist.
  const canSpawnWorker =
    hasRequire &&
    typeof Worker !== 'undefined' &&
    typeof (require as any).resolve === 'function';
  if (canSpawnWorker) {
    try {
      const workerPath = require.resolve('elkjs/lib/elk-worker.min.js');
      return new (ELKApi as any)({
        workerFactory: () => new Worker(workerPath),
      });
    } catch (err) {
      console.warn('[ProcessLayoutService] worker-thread ELK init failed; falling back to bundled', err);
    }
  }

  try {
    const ELKBundled = await loadBundledElk();
    if (ELKBundled) return new ELKBundled();
  } catch (err) {
    console.warn(
      '[ProcessLayoutService] elk.bundled unavailable; using deterministic layered layout',
      err,
    );
  }
  return DETERMINISTIC_FALLBACK_ELK;
}

/**
 * Deterministic, dependency-free layered layout used when no real ELK
 * engine is available (e.g. Cloudflare workerd: no `Worker`, and
 * `elk.bundled.js` itself references `Worker` at construction so it
 * throws `_Worker is not a constructor`). Without this, server-side
 * diagram materialization returned the graph unchanged — every node
 * stacked at the origin, producing an unusable diagram.
 *
 * This implements a longest-path layering (Sugiyama rank assignment
 * without crossing minimisation) which is good enough for the
 * RDM/process flows we materialize: nodes spread across ranks along
 * the flow axis, siblings spaced along the cross axis. It emits an
 * ELK-shaped result (children with x/y, edges with a single section)
 * so `extractPositions`/`extractEdgeEndpoints` and the swimlane
 * banding logic keep working untouched.
 */
function deterministicLayout(graph: any): any {
  const children: any[] = Array.isArray(graph?.children) ? graph.children : [];
  const edges: any[] = Array.isArray(graph?.edges) ? graph.edges : [];
  if (children.length === 0) return graph;

  const opts = graph?.layoutOptions ?? {};
  const direction = String(opts['elk.direction'] ?? 'RIGHT').toUpperCase();
  const isVertical = direction === 'DOWN' || direction === 'UP';
  const nodeSpacing = Number(opts['elk.spacing.nodeNode']) || 80;
  const rankSpacing =
    Number(opts['elk.layered.spacing.nodeNodeBetweenLayers']) || 100;

  const byId = new Map<string, any>();
  for (const c of children) byId.set(c.id, c);

  const outgoing = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  for (const c of children) indegree.set(c.id, 0);
  for (const e of edges) {
    const from = e?.sources?.[0];
    const to = e?.targets?.[0];
    if (!byId.has(from) || !byId.has(to) || from === to) continue;
    if (!outgoing.has(from)) outgoing.set(from, []);
    outgoing.get(from)!.push(to);
    indegree.set(to, (indegree.get(to) ?? 0) + 1);
  }

  // Longest-path rank assignment with a cycle-safe iteration cap.
  const rank = new Map<string, number>();
  for (const c of children) rank.set(c.id, 0);
  const order = children.map((c) => c.id);
  for (let pass = 0; pass < children.length; pass++) {
    let changed = false;
    for (const id of order) {
      const r = rank.get(id) ?? 0;
      for (const next of outgoing.get(id) ?? []) {
        if ((rank.get(next) ?? 0) < r + 1) {
          rank.set(next, r + 1);
          changed = true;
        }
      }
    }
    if (!changed) break;
  }

  // Group by rank, preserving model order within each rank.
  const ranks = new Map<number, any[]>();
  for (const c of children) {
    const r = rank.get(c.id) ?? 0;
    if (!ranks.has(r)) ranks.set(r, []);
    ranks.get(r)!.push(c);
  }

  const crossSize = (n: any): number =>
    isVertical ? Number(n.width) || 120 : Number(n.height) || 80;
  const flowSize = (n: any): number =>
    isVertical ? Number(n.height) || 80 : Number(n.width) || 120;

  const sortedRanks = [...ranks.entries()].sort((a, b) => a[0] - b[0]);
  let flowOffset = 0;
  for (const [, group] of sortedRanks) {
    let maxFlow = 0;
    for (const n of group) maxFlow = Math.max(maxFlow, flowSize(n));

    let crossOffset = 0;
    for (const n of group) {
      const flowPos = flowOffset + (maxFlow - flowSize(n)) / 2;
      if (isVertical) {
        n.x = crossOffset;
        n.y = flowPos;
      } else {
        n.x = flowPos;
        n.y = crossOffset;
      }
      crossOffset += crossSize(n) + nodeSpacing;
    }
    flowOffset += maxFlow + rankSpacing;
  }

  // Emit straight-line sections so edge-endpoint extraction works.
  for (const e of edges) {
    const from = byId.get(e?.sources?.[0]);
    const to = byId.get(e?.targets?.[0]);
    if (!from || !to) continue;
    const start = {
      x: (from.x ?? 0) + (Number(from.width) || 120) / 2,
      y: (from.y ?? 0) + (Number(from.height) || 80) / 2,
    };
    const end = {
      x: (to.x ?? 0) + (Number(to.width) || 120) / 2,
      y: (to.y ?? 0) + (Number(to.height) || 80) / 2,
    };
    e.sections = [{ startPoint: start, endPoint: end }];
  }

  return graph;
}

const DETERMINISTIC_FALLBACK_ELK = {
  layout: async (graph: any) => deterministicLayout(graph),
};

async function loadBundledElk(): Promise<(new () => { layout: (graph: any) => Promise<any> }) | null> {
  // `elk.bundled.js` is CommonJS; both ESM (CF Workers, modern browsers)
  // and CJS callers get a constructor via the default export.
  const mod = (await import('elkjs/lib/elk.bundled.js')) as unknown as
    | (new () => { layout: (graph: any) => Promise<any> })
    | { default: new () => { layout: (graph: any) => Promise<any> } };
  if (typeof mod === 'function') return mod;
  if (mod && typeof (mod as any).default === 'function') return (mod as any).default;
  return null;
}

/**
 * Layout service for flowchart diagrams using ELK.js
 *
 * Supports multiple layout strategies:
 * - left-to-right: Horizontal flow (default for flowcharts)
 * - top-to-bottom: Vertical flow
 * - hierarchical: Tree layout with levels
 * - compact: Minimize whitespace
 */
export class ProcessLayoutService {
  // ELK is initialized lazily so we can `await` the dynamic import
  // path used in CF Workers / pure-ESM runtimes. The promise is
  // memoized so repeated layouts share a single engine instance.
  private elkPromise: Promise<{ layout: (graph: any) => Promise<any> }> | null = null;

  private getElk(): Promise<{ layout: (graph: any) => Promise<any> }> {
    if (!this.elkPromise) this.elkPromise = createElk();
    return this.elkPromise;
  }

  /**
   * Apply layout to entire flowchart
   *
   * @param nodes - All nodes in the diagram
   * @param edges - All edges in the diagram
   * @param options - Layout configuration
   * @returns Map of node IDs to their new positions
   */
  async applyLayout(
    nodes: Node[],
    edges: Edge[],
    options: LayoutOptions
  ): Promise<LayoutResult> {
    const nodeSpacing = options.nodeSpacing ?? 80;
    const rankSpacing = options.rankSpacing ?? 100;

    // Filter to only flowchart nodes and sequence_flow edges
    const flowchartNodes = nodes.filter(n => {
      const type = (n as any).type;
      return type === 'process' || type === 'decision' || type === 'terminator' ||
             type === 'data' || type === 'document' || type === 'flowchart-shape' || type === 'bpmn-shape';
    });

    const flowchartNodeIds = new Set(flowchartNodes.map(n => n.id));
    const flowchartEdges = edges.filter(e => {
      const etype = (e as any).type;
      const typeOk = etype === 'sequence_flow' || etype === 'message_flow' ||
             etype === 'exception_flow' || etype === 'data_association' ||
             ((e as any).category === 'flowchart' && etype === 'line');
      // Only include edges where both endpoints exist in the filtered node set
      return typeOk && flowchartNodeIds.has(e.from) && flowchartNodeIds.has(e.to);
    });

    if (flowchartNodes.length === 0) {
      return { nodePositions: new Map(), edgeEndpoints: new Map() };
    }

    // Convert to ELK graph format
    const elkGraph = this.convertToElkGraph(flowchartNodes, flowchartEdges, options, nodeSpacing, rankSpacing);

    // Apply layout. The engine resolution in createElk() can still hand
    // back a real ELK whose `layout()` throws at call time (workerd
    // exposes a non-constructable `Worker`, so the worker-thread engine
    // only fails when ELK invokes the factory). Catch that here and run
    // the deterministic layered fallback so materialization never ships
    // an origin-stacked diagram.
    const elk = await this.getElk();
    let layoutedGraph: any;
    try {
      layoutedGraph = await elk.layout(elkGraph);
    } catch (err) {
      console.warn(
        '[ProcessLayoutService] ELK layout threw at call time; using deterministic layered layout',
        err,
      );
      layoutedGraph = deterministicLayout(elkGraph);
    }
    return { nodePositions: this.extractPositions(layoutedGraph), edgeEndpoints: this.extractEdgeEndpoints(layoutedGraph) };
  }

  private extractPositions(layoutedGraph: any): Map<string, { x: number; y: number }> {
    const nodePositions = new Map<string, { x: number; y: number }>();
    if (layoutedGraph?.children) {
      for (const child of layoutedGraph.children) {
        if (child.id && typeof child.x === 'number' && typeof child.y === 'number') {
          nodePositions.set(child.id, { x: child.x, y: child.y });
        }
      }
    }
    return nodePositions;
  }

  private extractEdgeEndpoints(layoutedGraph: any): Map<string, ElkEdgeEndpoints> {
    const edgeEndpoints = new Map<string, ElkEdgeEndpoints>();
    const edges = layoutedGraph?.edges;
    if (!Array.isArray(edges)) return edgeEndpoints;

    const asPoint = (p: any): ElkPoint | null => {
      if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') return null;
      return { x: p.x, y: p.y };
    };

    for (const e of edges) {
      const id = e?.id;
      if (typeof id !== 'string' || id.length === 0) continue;

      const sections = Array.isArray(e?.sections) ? e.sections : [];
      const firstSection = sections[0];
      const lastSection = sections.length > 0 ? sections[sections.length - 1] : undefined;

      const startRaw = firstSection?.startPoint ?? firstSection?.start ?? e?.startPoint;
      const endRaw = (lastSection ?? firstSection)?.endPoint ?? (lastSection ?? firstSection)?.end ?? e?.endPoint;

      const start = asPoint(startRaw);
      const end = asPoint(endRaw);
      if (!start || !end) continue;

      edgeEndpoints.set(id, { start, end });
    }

    return edgeEndpoints;
  }

  /**
   * Convert diagram nodes/edges to ELK graph format
   */
  private convertToElkGraph(
    nodes: Node[],
    edges: Edge[],
    options: LayoutOptions,
    nodeSpacing: number,
    rankSpacing: number
  ): any {
    // Get node dimensions
    const nodeMap = new Map<string, { width: number; height: number }>();
    for (const node of nodes) {
      const type = (node as any).type;
      const manifest = getShapeManifest('flowchart', type);
      const defaultDims = manifest?.defaultDimensions || { width: 120, height: 80 };
      const width = (node.w ?? node.width ?? defaultDims.width) as number;
      const height = (node.h ?? node.height ?? defaultDims.height) as number;
      nodeMap.set(node.id, { width, height });
    }

    // Build ELK graph structure
    const elkNodes = nodes.map(node => {
      const dims = nodeMap.get(node.id)!;
      return {
        id: node.id,
        width: dims.width,
        height: dims.height,
      };
    });

    const elkEdges = edges.map(edge => ({
      id: edge.id,
      sources: [edge.from],
      targets: [edge.to],
    }));

    // Determine ELK algorithm and direction based on strategy
    let algorithm = 'layered';
    let direction = 'RIGHT'; // left-to-right

    switch (options.strategy) {
      case 'left-to-right':
        algorithm = 'layered';
        direction = 'RIGHT';
        break;
      case 'top-to-bottom':
        algorithm = 'layered';
        direction = 'DOWN';
        break;
      case 'hierarchical':
        algorithm = 'layered';
        direction = 'DOWN';
        break;
      case 'compact':
        algorithm = 'force';
        direction = 'UNDEFINED';
        break;
    }

    return {
      id: 'root',
      children: elkNodes,
      edges: elkEdges,
      layoutOptions: {
        'elk.algorithm': algorithm,
        'elk.direction': direction,
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.spacing.nodeNode': String(nodeSpacing),
        'elk.layered.spacing.nodeNodeBetweenLayers': String(rankSpacing),
        'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        'elk.layered.cycleBreaking.strategy': 'GREEDY',
        'elk.spacing.edgeNode': '20',
        'elk.spacing.edgeEdge': '15',
        'elk.layered.spacing.edgeNodeBetweenLayers': '20',
        'elk.layered.spacing.edgeEdgeBetweenLayers': '10',
        'elk.edgeLabels.placement': 'CENTER',
        'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
        // Compaction tames the wide horizontal spread we saw on
        // process imports — gateways with cross-lane back-edges left
        // huge empty rank gaps until ELK was asked to compact L/R
        // post-placement. Same option carried the network/CFN
        // imports out of "12-column rows" land (commit b4a875703 §8a).
        'elk.layered.compaction.postCompaction.strategy': 'LEFT_RIGHT_CONSTRAINT_LOCKING',
      },
    };
  }

  /**
   * Incremental layout after adding/removing nodes
   * For M1, this falls back to full layout
   *
   * @param nodes - All nodes in the diagram
   * @param edges - All edges in the diagram
   * @param changedNodeIds - IDs of nodes that were added/removed/modified
   * @param options - Layout configuration
   * @returns Map of node IDs to their new positions
   */
  async incrementalLayout(
    nodes: Node[],
    edges: Edge[],
    changedNodeIds: string[],
    options: LayoutOptions
  ): Promise<LayoutResult> {
    const full = await this.applyLayout(nodes, edges, options);
    if (changedNodeIds.length === 0) return full;

    const affected = new Set<string>(changedNodeIds);
    edges.forEach((e) => {
      if (affected.has(e.from) || affected.has(e.to)) {
        affected.add(e.from);
        affected.add(e.to);
      }
    });

    const filtered = new Map<string, { x: number; y: number }>();
    for (const [id, pos] of full.nodePositions.entries()) {
      if (affected.has(id)) {
        filtered.set(id, pos);
      }
    }
    return { nodePositions: filtered, edgeEndpoints: full.edgeEndpoints };
  }
}
